import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import type {
  DocumentVerificationsResponse,
  VerificationFileBlobRequest,
  VerificationFileBlobResponse,
} from './types';

export const submitVerification = async (dispatch: any, data: any) => {
  const headers = {
    'Content-Type': 'multipart/form-data',
  };
  const response = await requestHandler({
    method: 'post',
    url: `internal/invoiceConfirmation/batch`,
    data: data.data,
    headers,
    dispatch,
  });
  if (response) return response.data;
};

export const fetchAllVerifications = async (
  dispatch: Dispatch,
  queryParams: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/invoiceConfirmation?${queryParams}`,
    dispatch,
  });
  if (response) return response.data;
};

export const fetchAllVerificationsByOperation = async (
  dispatch: Dispatch,
  queryParams: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/invoiceConfirmation/byRequest?${queryParams}`,
    dispatch,
  });
  if (response) return response.data;
};

export const fetchVerificationsPortfolio = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/invoiceConfirmation/portfolio`,
    dispatch,
  });
  if (response) return response.data;
};

export const unAssignVerifierToOperations = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/invoiceConfirmation/unAssignVerifier`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const assignVerifierToOperations = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/invoiceConfirmation/assignVerifier`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getDocumentVerifications = async (
  dispatch: Dispatch,
  data: { documentId: string },
): Promise<DocumentVerificationsResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/invoiceConfirmation/document/${data.documentId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getDocumentVerificationsQuery = (
  dispatch: Dispatch,
  documentId: string,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ['documentConfirmation', 'documentVerifications', documentId],
    queryFn: async (): Promise<DocumentVerificationsResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/invoiceConfirmation/document/${documentId}`,
        dispatch,
      });
      if (response) return response.data;
    },
    enabled: options?.enabled !== false && !!documentId,
    retry: 1,
  });

export const downloadVerificationFile = async (
  dispatch: Dispatch,
  fileKey: string,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/invoiceConfirmation/download-verification-file`,
    data: { fileKey },
    dispatch,
  });
  if (response) return response.data;
};

export const getVerificationFileAsBlobQuery = (
  dispatch: Dispatch,
  fileKey: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['documentConfirmation', 'verificationFileBlob', fileKey],
    queryFn: async (): Promise<VerificationFileBlobResponse> => {
      const data: VerificationFileBlobRequest = { fileKey };
      const response = await requestHandler({
        method: 'post',
        url: 'internal/invoiceConfirmation/verification-file/blob',
        data,
        dispatch,
        responseType: 'blob',
      });
      if (!response) {
        throw new Error('No se pudo obtener el archivo');
      }
      const contentType =
        response.headers['content-type'] ?? 'application/octet-stream';
      return {
        blob: response.data as Blob,
        contentType,
      };
    },
    enabled: options?.enabled !== false && !!fileKey,
    retry: false,
    gcTime: 0,
  });
};

export const deleteVerification = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['documentConfirmation', 'deleteVerification'],
    mutationFn: async (id: number) => {
      const response = await requestHandler({
        method: 'delete',
        url: `internal/invoiceConfirmation/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: async () => {
      showAlert(dispatch, 'success', 'Verificación eliminada con éxito');
      await queryClient.invalidateQueries({
        queryKey: ['documentConfirmation', 'documentVerifications'],
      });
    },
  });
};
